import {
  extractJwtFromLoginBody,
  extractSetupTokenFromBody,
  extractUserFromAuthBody,
} from '@/lib/extractLoginJwt'
import {
  clearSignupSetupSession,
  saveSignupSetupToSession,
  savePasswordResetToSession,
  clearPasswordResetSession,
} from '@/lib/signupSession'
// Re-exported so callers can reach these without pulling from signupSession directly.
export { clearPasswordResetSession, clearSignupSetupSession }
import {
  api,
  AUTH_TOKEN_STORAGE_KEY,
  AUTH_TRANSPORT_STORAGE_KEY,
  type ApiError,
} from '@/services/api'
import { PASSWORD_API_PATHS } from '@/services/authPasswordPaths'

export type AuthRole = 'creator' | 'validator'

/** Re-export — edit routes in `authPasswordPaths.ts`. */
export { PASSWORD_API_PATHS }

export type MockUser = {
  id: string
  email: string
  /** Public profile slug (e.g. "amarnath-1234"). Set on first profile completion; may be absent for legacy/older sessions. */
  handle?: string | null
  full_name?: string | null
  /** Backend flag (from GET /user/me) — gates admin-only UI like Report Review. */
  is_admin?: boolean
}

export type MockAuthResult = {
  /** Empty when the API uses HttpOnly cookies only; `launchpad_auth_transport` is set to `cookie`. */
  token: string
  user: MockUser
  role: AuthRole
  /** Backend flag — false means user still needs to complete profile (city/industry/profession/etc). */
  profileComplete?: boolean
  /** Populated by Google sign-in (from Google's userinfo). */
  fullName?: string
}

export type CompleteProfileInput = {
  // Required core
  fullName: string
  industry: string
  profession: string
  // Optional / skippable — completable later in Profile
  persona?: string
  industryGroup?: string
  pronoun?: string
  ageRange?: string
  location?: string
  startupStatus?: string
  companyName?: string
  companyNamePublic?: boolean
  startupStage?: string
  businessSegment?: string
  lookingFor?: string[]
  mobileNumber?: string
  newsletterOptIn?: boolean
}

/** Final onboarding step: PATCH profile only (after password was set via `authSetPasswordSignup`). */
export type CompleteSignupProfilePayload = {
  email: string
  role: AuthRole
  profile: CompleteProfileInput
  /** From set-password response body when the API returns a JWT; omit when session is cookie-only. */
  accessToken?: string | null
}

const AUTH_ROLE_KEY = 'authRole'

const isApiConfigured = () => Boolean(import.meta.env.VITE_API_BASE_URL?.trim())

function getApiErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as ApiError).message === 'string'
  ) {
    return (err as ApiError).message
  }
  return 'Something went wrong. Please try again.'
}

/** Step 1: backend sends OTP to email */
export async function requestSignupOtp(email: string): Promise<void> {
  clearSignupSetupSession()
  if (!isApiConfigured()) {
    throw new Error(
      'API is not configured. Set VITE_API_BASE_URL in .env (e.g. http://localhost:3000/api/v1).',
    )
  }
  try {
    await api.post<{ message?: string }>('/auth/signup', { email })
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

/** Step 2: exchange OTP for short-lived setup JWT (stored in sessionStorage for the signup tab). */
export async function verifySignupOtp(
  email: string,
  otp: string,
): Promise<{ setupToken: string }> {
  if (!isApiConfigured()) {
    throw new Error('API is not configured. Set VITE_API_BASE_URL in .env.')
  }
  try {
    const { data } = await api.post<unknown>('/auth/verify-otp', {
      email,
      otp,
      purpose: 'signup',
    })
    const setupToken =
      extractSetupTokenFromBody(data) ?? extractJwtFromLoginBody(data)
    if (!setupToken) {
      throw new Error('Invalid response from server (no setup token).')
    }
    saveSignupSetupToSession(email, setupToken)
    return { setupToken }
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

const authHeader = (bearer: string) => ({ Authorization: `Bearer ${bearer}` })

/** Bearer from signup payload or localStorage (covers cookie + JWT backends after set-password). */
function resolveAccessTokenForMe(
  explicit: string | null | undefined,
): string | null {
  const fromPayload = explicit?.trim() ?? ''
  if (fromPayload) return fromPayload
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)?.trim() ?? ''
  return stored || null
}

type UpdateMeBody = {
  full_name?: string
  city?: string
  age_range?: string
  pronoun?: string
  industry?: string
  industry_group?: string
  profession?: string
  persona?: string
  startup_status?: string
  company_name?: string
  company_name_public?: boolean
  startup_stage?: string
  business_segment?: string
  looking_for?: string[]
  mobile_number?: string
  newsletter_opt_in?: boolean
}

/** Profile fields for PATCH /user/me (Bearer access JWT and/or session cookie, depending on backend). */
async function updateCurrentUserProfile(
  profile: CompleteProfileInput,
): Promise<void> {
  // The master_pronouns table stores the display label "Prefer not to say"
  // but the users.pronoun ENUM (and the matching Joi validator) expects the
  // snake-case value `prefer_not_to_say`. Map here so a user who selects
  // "Prefer not to say" doesn't get a 400 on finalising their profile.
  const pronoun =
    profile.pronoun === 'Prefer not to say'
      ? 'prefer_not_to_say'
      : profile.pronoun

  // Partial PATCH — only send fields the user actually filled (the wizard's
  // optional steps may be skipped). Empty strings are dropped.
  const body: UpdateMeBody = {}
  const str = (v?: string) => (v !== undefined && v !== '' ? v : undefined)
  if (str(profile.fullName)) body.full_name = profile.fullName
  if (str(profile.persona)) body.persona = profile.persona
  if (str(profile.industry)) body.industry = profile.industry
  if (str(profile.industryGroup)) body.industry_group = profile.industryGroup
  if (str(pronoun)) body.pronoun = pronoun
  if (str(profile.ageRange)) body.age_range = profile.ageRange
  if (str(profile.location)) body.city = profile.location
  if (str(profile.profession)) body.profession = profile.profession
  if (str(profile.startupStatus)) body.startup_status = profile.startupStatus
  if (str(profile.companyName)) body.company_name = profile.companyName
  if (profile.companyNamePublic !== undefined)
    body.company_name_public = profile.companyNamePublic
  if (str(profile.startupStage)) body.startup_stage = profile.startupStage
  if (str(profile.businessSegment))
    body.business_segment = profile.businessSegment
  if (profile.lookingFor && profile.lookingFor.length)
    body.looking_for = profile.lookingFor
  if (str(profile.mobileNumber)) body.mobile_number = profile.mobileNumber
  if (profile.newsletterOptIn !== undefined)
    body.newsletter_opt_in = profile.newsletterOptIn

  await api.patch('/user/me', body)
}
type SetPasswordApiBody = {
  token?: string
  user?: { id?: string; email?: string }
}

export async function authSetPasswordSignup(
  setupToken: string,
  password: string,
  email: string,
): Promise<{ accessToken: string | null; user: MockUser }> {
  if (!isApiConfigured()) {
    await new Promise((r) => setTimeout(r, 400))
    return {
      accessToken: `fake_token_${Math.random().toString(16).slice(2)}`,
      user: {
        id: `user_${Math.random().toString(16).slice(2)}`,
        email: email.trim(),
      },
    }
  }
  return postSetPasswordWithSetupToken(setupToken, password, email)
}

async function postSetPasswordWithSetupToken(
  setupToken: string,
  password: string,
  fallbackEmail: string,
): Promise<{ accessToken: string | null; user: MockUser }> {
  const { data } = await api.post<SetPasswordApiBody>(
    '/auth/set-password',
    { password },
    { headers: authHeader(setupToken) },
  )

  let accessToken = extractJwtFromLoginBody(data)
  if (accessToken?.trim() === '') accessToken = null
  if (typeof window !== 'undefined') {
    if (accessToken?.trim()) {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, accessToken.trim())
      localStorage.setItem(AUTH_TRANSPORT_STORAGE_KEY, 'jwt')
    } else {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
      localStorage.setItem(AUTH_TRANSPORT_STORAGE_KEY, 'cookie')
    }
  }

  let user = extractUserFromAuthBody(data, fallbackEmail)
  if (!user) {
    try {
      const { data: me } = await api.get<unknown>('/user/me')
      user = extractUserFromAuthBody(me, fallbackEmail)
    } catch {
      /* cookie session may not expose GET /user/me yet */
    }
  }
  if (!user) {
    user = { id: fallbackEmail.trim(), email: fallbackEmail.trim() }
  }

  return { accessToken: accessToken?.trim() ?? null, user }
}

/**
 * Login for JWT (used after email/password signup is complete).
 */
export type LoginApiUser = {
  id: string
  full_name: string
  email: string
  user_type: string
  auth_provider: string
  is_email_verified: boolean
  profile_complete: boolean
}

type LoginApiResponse = {
  success: boolean
  data: { user: LoginApiUser; token?: string }
}

async function loginForJwt(
  email: string,
  password: string,
): Promise<{ token: string; user: LoginApiUser }> {
  try {
    const { data } = await api.post<LoginApiResponse>('/auth/login', {
      email,
      password,
    })

    const jwt = extractJwtFromLoginBody(data)
    const user = data?.data?.user

    if (!user) {
      throw new Error('Login succeeded but no user data returned.')
    }

    return { token: jwt ?? '', user }
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

/**
 * After `authSetPasswordSignup`: PATCH profile. Uses Bearer `accessToken` if provided, else session cookie.
 */
export async function authCompleteSignupProfile(
  payload: CompleteSignupProfilePayload,
): Promise<MockAuthResult> {
  if (typeof window !== 'undefined') {
    localStorage.setItem(AUTH_ROLE_KEY, payload.role)
  }

  const accessToken = resolveAccessTokenForMe(payload.accessToken)

  if (!isApiConfigured()) {
    await new Promise((r) => setTimeout(r, 600))
    const user: MockUser = {
      id: `user_${Math.random().toString(16).slice(2)}`,
      email: payload.email,
    }
    const token =
      accessToken ?? `fake_token_${Math.random().toString(16).slice(2)}`
    return { token, user, role: payload.role }
  }

  try {
    await updateCurrentUserProfile(payload.profile)
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }

  let user: MockUser
  try {
    const { data: me } = await api.get<unknown>('/user/me')
    user = extractUserFromAuthBody(me, payload.email) ?? {
      id: payload.email.trim(),
      email: payload.email.trim(),
    }
  } catch {
    user = { id: payload.email.trim(), email: payload.email.trim() }
  }

  const token = accessToken ?? ''
  return { token, user, role: payload.role }
}

type GoogleAuthResponse = {
  success: boolean
  data: { user: LoginApiUser; token?: string }
}

/**
 * Exchange a Google credential for an app session. Backend sets an HttpOnly cookie.
 * Pass either an ID token (GIS credential JWT) or an OAuth2 access token.
 */
export async function authGoogle(
  credential: { idToken: string } | { accessToken: string },
): Promise<MockAuthResult> {
  if (!isApiConfigured()) {
    throw new Error('API is not configured. Set VITE_API_BASE_URL in .env.')
  }
  try {
    const { data } = await api.post<GoogleAuthResponse>(
      '/auth/google',
      credential,
    )
    const jwt = extractJwtFromLoginBody(data) ?? ''
    const apiUser = data?.data?.user
    if (!apiUser) {
      throw new Error('Google sign-in succeeded but no user data returned.')
    }
    const role: AuthRole =
      apiUser.user_type === 'validator' ? 'validator' : 'creator'
    if (typeof window !== 'undefined') {
      localStorage.setItem(AUTH_ROLE_KEY, role)
    }
    return {
      token: jwt,
      user: { id: apiUser.id, email: apiUser.email },
      role,
      profileComplete: apiUser.profile_complete,
      fullName: apiUser.full_name,
    }
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

/**
 * Tells the backend to clear the HttpOnly accessToken cookie.
 * Fails silently — the user must be logged out on the client regardless of
 * whether the server call succeeds (offline, expired token, etc).
 */
export async function authLogout(): Promise<void> {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(AUTH_TRANSPORT_STORAGE_KEY)
    localStorage.removeItem(AUTH_ROLE_KEY)
  }

  if (!isApiConfigured()) return
  try {
    await api.post('/auth/logout')
  } catch {
    // Swallow — client-side cleanup still proceeds.
  }
}

export async function authLogin(
  email: string,
  password: string,
): Promise<MockAuthResult> {
  const role = (
    typeof window !== 'undefined' ? localStorage.getItem(AUTH_ROLE_KEY) : null
  ) as AuthRole | null
  const safeRole: AuthRole = role === 'validator' ? 'validator' : 'creator'

  if (!isApiConfigured()) {
    await new Promise((resolve) => setTimeout(resolve, 600))
    const user: MockUser = {
      id: `user_${email.toLowerCase().replace(/[^a-z0-9]/g, '')}_${Math.random().toString(16).slice(2)}`,
      email,
    }
    const token = `fake_token_${Math.random().toString(16).slice(2)}`
    return { token, user, role: safeRole }
  }

  try {
    const { token, user: apiUser } = await loginForJwt(email, password)
    const resolvedRole: AuthRole =
      apiUser.user_type === 'validator' ? 'validator' : 'creator'
    return {
      token,
      user: { id: apiUser.id, email: apiUser.email },
      role: resolvedRole,
      profileComplete: apiUser.profile_complete,
      fullName: apiUser.full_name,
    }
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

// --- Password flows (UI calls these; bodies match login-style keys — adjust if your API differs) ---

export type RequestPasswordResetInput = { email: string }

export type ConfirmPasswordResetInput = {
  resetToken: string
  newPassword: string
}

export type ChangePasswordInput = {
  currentPassword: string
  newPassword: string
}

export async function requestPasswordReset(
  input: RequestPasswordResetInput,
): Promise<void> {
  if (!isApiConfigured()) {
    throw new Error('API is not configured. Set VITE_API_BASE_URL in .env.')
  }
  try {
    await api.post(PASSWORD_API_PATHS.requestReset, { email: input.email })
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

export async function verifyPasswordResetOtp(input: {
  email: string
  otp: string
}): Promise<{ resetToken: string }> {
  if (!isApiConfigured()) {
    throw new Error('API is not configured. Set VITE_API_BASE_URL in .env.')
  }
  try {
    const { data } = await api.post<{ resetToken: string }>(
      PASSWORD_API_PATHS.verifyOtp,
      {
        email: input.email,
        otp: input.otp,
        purpose: 'password_reset',
      },
    )
    savePasswordResetToSession(input.email, data.resetToken)
    return { resetToken: data.resetToken }
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

export async function confirmPasswordReset(
  input: ConfirmPasswordResetInput,
): Promise<void> {
  if (!isApiConfigured()) {
    throw new Error('API is not configured. Set VITE_API_BASE_URL in .env.')
  }
  try {
    await api.post(
      PASSWORD_API_PATHS.confirmReset,
      { password: input.newPassword },
      { headers: { Authorization: `Bearer ${input.resetToken}` } },
    )
    clearPasswordResetSession()
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}

/** Uses stored JWT via `api` interceptor and/or session cookie. */
export async function changePassword(
  input: ChangePasswordInput,
): Promise<void> {
  if (!isApiConfigured()) {
    throw new Error('API is not configured. Set VITE_API_BASE_URL in .env.')
  }
  try {
    await api.post(PASSWORD_API_PATHS.change, {
      CurrentPassword: input.currentPassword,
      NewPassword: input.newPassword,
    })
  } catch (e) {
    throw new Error(getApiErrorMessage(e))
  }
}
